import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";
import { logAction } from "./ALQOSLogger";

// ─── CONSTANTES ───────────────────────────────────────────────
const STORAGE_FMEA = 'alqos_fmea';
const STORAGE_PC = 'alqos_plancontrol';
const STORAGE_MAQUINAS = 'alqos_maquinas';
const NPR_CRITICO_ALTO = 200;
const NPR_CRITICO = 100;
const NPR_MODERADO = 50;

const LOG = {
  info: (msg, data) => console.log(`[FMEA][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[FMEA][ERROR] ${msg}`, err || ''),
  warn: (msg, data) => console.warn(`[FMEA][WARN] ${msg}`, data || ''),
};

const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { LOG.error(`get ${key}`, e); return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { LOG.error(`set ${key}`, e); return false; } },
};

const procesosGraficos = ["Impresión Offset", "Pre Prensa", "Troquelado", "Laminado", "Barnizado", "Corte", "Estuchado / Armado", "Control de Calidad", "Expedición", "Otro"];
const estadosFMEA = { abierto: { label: "🔓 Abierto", color: "#e63946" }, en_proceso: { label: "🔵 En proceso", color: "#3b82f6" }, cerrado: { label: "✅ Cerrado", color: "#22c55e" } };
// Fase 18 (2/2) -- AMFE de máquina (bridge desde Maquinas.js -- incorporarAlAMFE())
// convive en la misma tabla que el AMFE de proceso de siempre. Los registros viejos
// no tienen `tipo` -- se tratan como "proceso" (tipoDe() de acá abajo).
const tiposFMEA = { proceso: { label: "🏭 Proceso", color: "#7B5BF2" }, maquina: { label: "⚙️ Máquina", color: "#3b82f6" } };
function tipoDe(r) { return r?.tipo === "maquina" ? "maquina" : "proceso"; }

function generarIdFMEA(proceso, lista) {
  const proc = proceso?.replace(/\s+/g, "").slice(0, 6).toUpperCase() || "GEN";
  const num = String(lista.length + 1).padStart(3, "0");
  return `FMEA-${proc}-${num}`;
}

function calcularNPR(s, o, d) {
  const n = Number(s) * Number(o) * Number(d);
  return isNaN(n) ? 0 : n;
}

function nivelNPR(npr) {
  if (npr === null || npr === undefined) return { label: "⚪ Sin evaluar", color: "#9aabd4" };
  if (npr >= NPR_CRITICO_ALTO) return { label: "🔴 CRÍTICO ALTO", color: "#e63946" };
  if (npr >= NPR_CRITICO) return { label: "🟠 CRÍTICO", color: "#f97316" };
  if (npr >= NPR_MODERADO) return { label: "🟡 Moderado", color: "#f59e0b" };
  return { label: "🟢 Bajo", color: "#22c55e" };
}

function obtenerControlesVinculados(idFMEA) {
  try { return storage.get(STORAGE_PC).filter(c => c.idFMEARelacionado === idFMEA); }
  catch (e) { return []; }
}

// ─── EXPORTACIÓN ──────────────────────────────────────────────
function exportarCSV(datos, nombre) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = Object.keys(datos[0]).filter(k => !k.startsWith('_'));
    const csv = [keys.join(","), ...datos.map(d => keys.map(k => `"${String(d[k] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${nombre}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    LOG.info("CSV FMEA exportado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar CSV", err); }
}

function exportarPDF(datos, titulo) {
  try {
    if (!datos.length) { alert("No hay datos para exportar."); return; }
    const keys = ["idFMEA", "idRiesgo", "proceso", "producto", "modofalla", "efecto", "causa", "severidad", "ocurrencia", "deteccion", "npr", "nivelNPR", "responsable", "estado", "fechaCreacion"];
    const filas = datos.map(d => `<tr>${keys.map(k => `<td style="padding:4px 6px;border-bottom:.5px solid #ddd;font-size:9px">${d[k] || "—"}</td>`).join("")}</tr>`).join("");
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>body{font-family:Arial,sans-serif;font-size:10px;padding:20px}
    h1{color:#09184D;font-size:16px}h2{color:#7B5BF2;font-size:12px}
    table{width:100%;border-collapse:collapse;margin-top:16px}
    th{background:#09184D;color:white;padding:5px 6px;font-size:9px;text-align:left}
    tr:nth-child(even){background:#f7f7f7}</style></head>
    <body><h1>ALQOS™ — ${titulo}</h1>
    <h2>Análisis de Modo y Efecto de Falla</h2>
    <p style="font-size:10px;color:#808085">Generado: ${new Date().toLocaleString('es-AR')} | Total: ${datos.length} | Críticos NPR≥100: ${datos.filter(d => d.critico).length}</p>
    <table><thead><tr>${keys.map(k => `<th>${k}</th>`).join("")}</tr></thead>
    <tbody>${filas}</tbody></table>
    <p style="font-size:9px;color:#808085;margin-top:20px">ALQOS™ FMEA — Confidencial — ISO 9001:2015 §6.1</p>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close();
    setTimeout(() => w.print(), 500);
    LOG.info("PDF FMEA generado", { registros: datos.length });
  } catch (err) { LOG.error("Error exportar PDF", err); }
}

export default function FMEA({ onVolver, oscuro, usuario }) {
  const [registros, setRegistros] = useState([]);
const [cargandoFMEA, setCargandoFMEA] = useState(true);
useEffect(() => {
  async function cargar() {
    try {
      const { supabase } = await import('./supabase');
      const { data } = await supabase.from('fmeas').select('data');
      if (data && data.length > 0) setRegistros(data.map(r => r.data));
      else setRegistros(storage.get(STORAGE_FMEA));
    } catch(e) { setRegistros(storage.get(STORAGE_FMEA)); }
    setCargandoFMEA(false);
  }
  cargar();
}, []);
  // Catálogo de máquinas para poder vincular un FMEA de proceso a una
  // máquina puntual desde el alta normal -- antes solo existía el puente
  // automático desde Maquinas.js (incorporarAlAMFE(), tipo:"maquina").
  // Mismo patrón de solo lectura ya probado en TPMMantenimiento.js.
  const [maquinas, setMaquinas] = useState([]);
  useEffect(() => {
    async function cargarMaquinas() {
      try {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('maquinas').select('data');
        if (data?.length) setMaquinas(data.map(r => r.data));
        else setMaquinas(storage.get(STORAGE_MAQUINAS));
      } catch (e) { setMaquinas(storage.get(STORAGE_MAQUINAS)); }
    }
    cargarMaquinas();
  }, []);
  const [vista, setVista] = useState("listado");
  const [form, setForm] = useState({ proceso: "Impresión Offset", estado: "abierto", severidad: "", ocurrencia: "", deteccion: "" });
  const [detalle, setDetalle] = useState(null);
  const [confirmEliminar, setConfirmEliminar] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [errores, setErrores] = useState({});

  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const esAdmin = usuario?.rol === "Administrador";
  if (cargandoFMEA) return (
  <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:bg }}>
    <p style={{ color: oscuro?"white":"#09184D", fontSize:"16px" }}>Cargando FMEA...</p>
  </div>
);

  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${borde}`, fontSize: "13px", boxSizing: "border-box", marginTop: "4px", background: cardBg, color: texto, fontFamily: "sans-serif" };
  const lbl = { fontSize: "12px", color: oscuro ? "#9aabd4" : "#555", fontWeight: "600", display: "block", marginTop: "12px" };
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };

  const npr = calcularNPR(form.severidad, form.ocurrencia, form.deteccion);

  const validar = () => {
    const e = {};
    if (!form.proceso) e.proceso = "Requerido";
    if (!form.producto?.trim()) e.producto = "Requerido";
    if (!form.operacion?.trim()) e.operacion = "Requerido";
    if (!form.modofalla?.trim()) e.modofalla = "Requerido";
    if (!form.efecto?.trim()) e.efecto = "Requerido";
    if (!form.causa?.trim()) e.causa = "Requerido";
    if (!form.severidad || form.severidad < 1 || form.severidad > 10) e.severidad = "1-10";
    if (!form.ocurrencia || form.ocurrencia < 1 || form.ocurrencia > 10) e.ocurrencia = "1-10";
    if (!form.deteccion || form.deteccion < 1 || form.deteccion > 10) e.deteccion = "1-10";
    if (!form.responsable?.trim()) e.responsable = "Requerido";
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const guardar = () => {
    if (!validar()) return;
    try {
      const registrosActuales = storage.get(STORAGE_FMEA);
      const duplicado = registrosActuales.find(r =>
        r.proceso === form.proceso &&
        r.modofalla?.trim().toLowerCase() === form.modofalla?.trim().toLowerCase() &&
        r.producto?.trim().toLowerCase() === form.producto?.trim().toLowerCase() &&
        r.estado !== "cerrado"
      );
      if (duplicado) { alert(`⚠️ Ya existe un FMEA abierto para este modo de falla (${duplicado.idFMEA}).`); return; }
      const nprCalc = calcularNPR(form.severidad, form.ocurrencia, form.deteccion);
      const idFMEA = generarIdFMEA(form.proceso, registrosActuales);
      const nuevo = {
        ...form, id: Date.now(), idFMEA, idRiesgo: `${idFMEA}-R01`, tipo: "proceso",
        npr: nprCalc, nivelNPR: nivelNPR(nprCalc).label,
        critico: nprCalc >= NPR_CRITICO, criticoAlto: nprCalc >= NPR_CRITICO_ALTO,
        fechaCreacion: new Date().toISOString(),
        creadoPor: usuario?.nombre || "Sistema", rolCreador: usuario?.rol || "",
        estado: "abierto", version: 1, _source: "localStorage", _schema: "fmea_v1",
      };
      const nuevos = [...registrosActuales, nuevo];
      if (!storage.set(STORAGE_FMEA, nuevos)) { alert("Error al guardar."); return; }
      subirDatos(STORAGE_FMEA);
      logAction("FMEA", "crear", { entidad: "FMEA", idEntidad: nuevo.idFMEA, despues: { idFMEA: nuevo.idFMEA, npr: nuevo.npr, proceso: nuevo.proceso }, detalle: `FMEA creado NPR ${nuevo.npr}` }, usuario);
      LOG.info("FMEA guardado", { id: nuevo.idFMEA, npr: nuevo.npr });
      setRegistros(nuevos);
      setForm({ proceso: "Impresión Offset", estado: "abierto", severidad: "", ocurrencia: "", deteccion: "" });
      setErrores({}); setVista("listado");
    } catch (err) { LOG.error("Error al guardar FMEA", err); alert("Error inesperado."); }
  };

  const cerrarRegistro = (id) => {
    const reg = registros.find(r => r.id === id);
    if (!reg?.accionRecomendada?.trim()) { alert("No se puede cerrar sin acción recomendada."); return; }
    const controlesVinculados = obtenerControlesVinculados(reg.idFMEA);
    if (controlesVinculados.length > 0) {
      if (!window.confirm(`Este FMEA tiene ${controlesVinculados.length} control(es) vinculado(s). ¿Confirmás el cierre?`)) return;
    }
    const actualizados = registros.map(r => r.id === id ? { ...r, estado: "cerrado", fechaCierre: new Date().toISOString(), cerradoPor: usuario?.nombre } : r);
    setRegistros(actualizados);
    storage.set(STORAGE_FMEA, actualizados);
    subirDatos(STORAGE_FMEA);
    logAction("FMEA", "cerrar", { entidad: "FMEA", idEntidad: reg.idFMEA, antes: { estado: "abierto" }, despues: { estado: "cerrado" }, detalle: `FMEA cerrado` }, usuario);
    if (detalle?.id === id) setDetalle(prev => ({ ...prev, estado: "cerrado" }));
  };

  const cambiarEstado = (id, nuevoEstado) => {
    if (nuevoEstado === "cerrado") { cerrarRegistro(id); return; }
    const reg = registros.find(r => r.id === id);
    const actualizados = registros.map(r => r.id === id ? { ...r, estado: nuevoEstado } : r);
    setRegistros(actualizados);
    storage.set(STORAGE_FMEA, actualizados);
    subirDatos(STORAGE_FMEA);
    logAction("FMEA", "cambiar_estado", { entidad: "FMEA", idEntidad: reg?.idFMEA, antes: { estado: reg?.estado }, despues: { estado: nuevoEstado } }, usuario);
    if (detalle?.id === id) setDetalle(prev => ({ ...prev, estado: nuevoEstado }));
  };

  // Edición in-place, mismo patrón onBlur que Maquinas.js (actualizar()) --
  // vale igual para FMEA de proceso y de máquina. Si el campo editado es
  // parte del NPR (S/O/D), recalcula npr/nivelNPR/critico/criticoAlto acá
  // mismo -- pero solo cuando los 3 ya están completos, para no perder el
  // estado "sin evaluar" (npr: null) mientras Calidad todavía está
  // completando un AMFE de máquina recién incorporado desde un reporte.
  const actualizar = (id, campos) => {
    let registroActualizado = null;
    const nuevos = registros.map(r => {
      if (r.id !== id) return r;
      let actualizado = { ...r, ...campos };
      const tocaPuntuacion = ["severidad", "ocurrencia", "deteccion"].some(k => k in campos);
      if (tocaPuntuacion && actualizado.severidad !== "" && actualizado.ocurrencia !== "" && actualizado.deteccion !== "") {
        const nprCalc = calcularNPR(actualizado.severidad, actualizado.ocurrencia, actualizado.deteccion);
        actualizado = { ...actualizado, npr: nprCalc, nivelNPR: nivelNPR(nprCalc).label, critico: nprCalc >= NPR_CRITICO, criticoAlto: nprCalc >= NPR_CRITICO_ALTO };
      }
      registroActualizado = actualizado;
      return actualizado;
    });
    setRegistros(nuevos);
    storage.set(STORAGE_FMEA, nuevos);
    subirDatos(STORAGE_FMEA);
    if (detalle?.id === id) setDetalle(registroActualizado);
  };

  const eliminar = async (id) => {
    const reg = registros.find(r => r.id === id);
    const nuevos = registros.filter(r => r.id !== id);
    setRegistros(nuevos);
    storage.set(STORAGE_FMEA, nuevos);
    await eliminarDato(STORAGE_FMEA, id);
    logAction("FMEA", "eliminar", { entidad: "FMEA", idEntidad: reg?.idFMEA, antes: reg, detalle: "FMEA eliminado" }, usuario);
    setConfirmEliminar(null); setDetalle(null); setVista("listado");
  };

  const registrosFiltrados = registros.filter(r => {
    if (filtroTipo !== "todos" && tipoDe(r) !== filtroTipo) return false;
    if (filtro === "criticos") return r.critico;
    if (filtro === "todos") return true;
    return r.estado === filtro;
  });

  const resumen = {
    total: registros.length,
    criticos: registros.filter(r => r.critico).length,
    abiertos: registros.filter(r => r.estado === "abierto").length,
    nprPromedio: registros.length > 0 ? Math.round(registros.reduce((a, r) => a + (r.npr || 0), 0) / registros.length) : 0,
  };

  const Header = ({ titulo, onBack, extra }) => (
    <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver</button>
        <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>{titulo}</span>
      </div>
      {extra}
    </div>
  );

  if (confirmEliminar) return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "2rem", maxWidth: "360px", width: "90%", border: `1px solid ${borde}`, textAlign: "center" }}>
        <p style={{ fontSize: "32px", marginBottom: "1rem" }}>🔍</p>
        <p style={{ fontSize: "15px", fontWeight: "600", color: texto, marginBottom: ".5rem" }}>¿Eliminar este FMEA?</p>
        <p style={{ fontSize: "13px", color: subtexto, marginBottom: "1.5rem" }}>Los planes de control vinculados quedarán sin FMEA de referencia.</p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <button onClick={() => setConfirmEliminar(null)} style={{ padding: "10px 20px", background: "none", border: `1px solid ${borde}`, borderRadius: "10px", cursor: "pointer", color: texto, fontSize: "13px" }}>Cancelar</button>
          <button onClick={() => eliminar(confirmEliminar)} style={{ padding: "10px 20px", background: "#e63946", border: "none", borderRadius: "10px", cursor: "pointer", color: "white", fontSize: "13px", fontWeight: "600" }}>Eliminar</button>
        </div>
      </div>
    </div>
  );

  if (vista === "detalle" && detalle) {
    const controlesVinculados = obtenerControlesVinculados(detalle.idFMEA);
    return (
      <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
        <Header titulo={detalle.idFMEA}
          onBack={() => { setDetalle(null); setVista("listado"); }}
          extra={
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={() => exportarPDF([detalle], `FMEA_${detalle.idFMEA}`)}
                style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 10px", borderRadius: "8px", cursor: "pointer", fontSize: "12px" }}>📄 PDF</button>
              {esAdmin && <button onClick={() => setConfirmEliminar(detalle.id)}
                style={{ background: "rgba(230,57,70,.2)", border: "1px solid rgba(230,57,70,.4)", color: "#e63946", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "12px" }}>🗑 Eliminar</button>}
            </div>
          }
        />
        <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
          <div style={{ ...card, background: detalle.critico ? (oscuro ? "#2a0a0a" : "#fff0f0") : (oscuro ? "#0a1f5c" : "#F0EEFF"), border: `1px solid ${detalle.critico ? "#e63946" : (oscuro ? "#7B5BF2" : "#d4c8ff")}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
              <h3 style={{ fontSize: "13px", fontWeight: "600", color: detalle.critico ? "#e63946" : "#7B5BF2", margin: 0 }}>
                {detalle.critico ? "⚠️ FMEA CRÍTICO" : "Identificación FMEA"}
              </h3>
              <span style={{ fontSize: "12px", fontWeight: "700", padding: "4px 10px", borderRadius: "10px", background: `${nivelNPR(detalle.npr).color}22`, color: nivelNPR(detalle.npr).color, border: `1px solid ${nivelNPR(detalle.npr).color}` }}>
                NPR: {detalle.npr ?? "—"} — {nivelNPR(detalle.npr).label}
              </span>
            </div>
            {[["ID FMEA", detalle.idFMEA], ["ID Riesgo", detalle.idRiesgo], ["Tipo", tiposFMEA[tipoDe(detalle)].label], ["Máquina vinculada", detalle.maquinaNombre], ["Creado por", `${detalle.creadoPor} — ${detalle.rolCreador}`], ["Fecha creación", detalle.fechaCreacion?.slice(0, 10)]].map(([k, v]) => v && (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ color: subtexto }}>{k}</span>
                <span style={{ color: k === "ID Riesgo" ? "#7B5BF2" : texto, fontWeight: k === "ID Riesgo" ? "700" : "500" }}>{v}</span>
              </div>
            ))}
            {tipoDe(detalle) === "maquina" ? (
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "4px 0", borderBottom: `0.5px solid ${borde}` }}>
                <span style={{ color: subtexto }}>Máquina</span>
                <span style={{ color: texto, fontWeight: "500" }}>{detalle.maquinaNombre}</span>
              </div>
            ) : (
              <>
                <span style={lbl}>Proceso</span>
                <select defaultValue={detalle.proceso || ""} disabled={!esAdmin} onChange={e => actualizar(detalle.id, { proceso: e.target.value })} style={inp}>
                  {procesosGraficos.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                {maquinas.length > 0 && (
                  <>
                    <span style={lbl}>Vincular a máquina cargada (opcional)</span>
                    <select defaultValue={detalle.maquinaId || ""} disabled={!esAdmin} onChange={e => {
                      const id = e.target.value ? Number(e.target.value) : null;
                      const m = maquinas.find(x => x.id === id);
                      actualizar(detalle.id, { maquinaId: id, maquinaNombre: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null });
                    }} style={inp}>
                      <option value="">— Sin vincular —</option>
                      {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
                    </select>
                  </>
                )}
              </>
            )}
            <span style={lbl}>Producto</span>
            <input defaultValue={detalle.producto || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { producto: e.target.value })} style={inp} />
            <span style={lbl}>Operación / Etapa</span>
            <input defaultValue={detalle.operacion || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { operacion: e.target.value })} style={inp} />
          </div>

          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#e63946", marginBottom: "12px" }}>Análisis de falla</h3>
            {[{ key: "modofalla", label: "Modo de falla" }, { key: "efecto", label: "Efecto de la falla" }, { key: "causa", label: "Causa" }, { key: "controlesActuales", label: "Controles actuales" }].map(({ key, label }) => (
              <div key={key}>
                <span style={lbl}>{label}</span>
                <textarea rows={2} defaultValue={detalle[key] || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { [key]: e.target.value })} style={{ ...inp, resize: "none" }} />
              </div>
            ))}
          </div>

          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>Puntuaciones (1 a 10)</h3>
            {[{ key: "severidad", label: "Severidad" }, { key: "ocurrencia", label: "Ocurrencia" }, { key: "deteccion", label: "Detección" }].map(({ key, label }) => {
              const v = detalle[key];
              return (
                <div key={key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `0.5px solid ${borde}`, gap: "10px" }}>
                  <span style={{ fontSize: "13px", color: subtexto }}>{label}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: `${(Number(v) / 10) * 80}px`, height: "6px", background: v >= 8 ? "#e63946" : v >= 5 ? "#f59e0b" : "#22c55e", borderRadius: "3px" }} />
                    <input type="number" min="1" max="10" defaultValue={v ?? ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { [key]: e.target.value })}
                      style={{ width: "56px", padding: "4px 6px", borderRadius: "6px", border: `1px solid ${borde}`, fontSize: "13px", fontWeight: "700", textAlign: "center", background: cardBg, color: texto }} />
                  </div>
                </div>
              );
            })}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontSize: "15px", fontWeight: "700" }}>
              <span style={{ color: texto }}>NPR Total</span>
              <span style={{ color: nivelNPR(detalle.npr).color, fontSize: "18px" }}>{detalle.npr ?? nivelNPR(detalle.npr).label}</span>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "12px" }}>Estado</h3>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {Object.entries(estadosFMEA).map(([val, { label, color }]) => (
                <button key={val} onClick={() => esAdmin && cambiarEstado(detalle.id, val)}
                  style={{ padding: "7px 12px", borderRadius: "10px", fontSize: "12px", cursor: esAdmin ? "pointer" : "default", fontWeight: "600", border: detalle.estado === val ? `2px solid ${color}` : `1px solid ${borde}`, background: detalle.estado === val ? `${color}22` : cardBg, color: detalle.estado === val ? color : subtexto }}>
                  {label}
                </button>
              ))}
            </div>
            {detalle.fechaCierre && <p style={{ fontSize: "11px", color: subtexto, marginTop: "8px" }}>Cerrado: {detalle.fechaCierre?.slice(0, 16).replace("T", " ")} por {detalle.cerradoPor}</p>}
          </div>

          <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0fff4", border: `1px solid ${oscuro ? "#22c55e44" : "#bbf7d0"}` }}>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#22c55e", marginBottom: "8px" }}>Acción recomendada</h3>
            <textarea rows={3} defaultValue={detalle.accionRecomendada || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { accionRecomendada: e.target.value })} style={{ ...inp, resize: "none", marginTop: 0 }} placeholder="Definí la acción para reducir el riesgo..." />
            <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
              <div style={{ flex: 1 }}>
                <span style={lbl}>Responsable</span>
                <input defaultValue={detalle.responsable || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { responsable: e.target.value })} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={lbl}>Fecha compromiso</span>
                <input type="date" defaultValue={detalle.fechaCompromiso || ""} disabled={!esAdmin} onBlur={e => actualizar(detalle.id, { fechaCompromiso: e.target.value })} style={inp} />
              </div>
            </div>
          </div>

          {controlesVinculados.length > 0 && (
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", marginBottom: "10px" }}>
                🔗 Controles vinculados ({controlesVinculados.length})
              </h3>
              {controlesVinculados.map(c => (
                <div key={c.id} style={{ padding: "8px 0", borderBottom: `0.5px solid ${borde}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: "#7B5BF2" }}>{c.idPC}</span>
                    <p style={{ fontSize: "12px", color: texto, margin: "2px 0 0" }}>{c.caracteristica}</p>
                    <p style={{ fontSize: "11px", color: subtexto, margin: 0 }}>{c.metodo} · {c.frecuencia}</p>
                  </div>
                  <span style={{ fontSize: "11px", fontWeight: "700", color: c.tipoCaracteristica === "critica" ? "#e63946" : c.tipoCaracteristica === "mayor" ? "#f59e0b" : "#22c55e" }}>
                    {c.tipoCaracteristica === "critica" ? "🔴 Crítica" : c.tipoCaracteristica === "mayor" ? "🟡 Mayor" : "🟢 Menor"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (vista === "nueva") return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <Header titulo="Nuevo FMEA" onBack={() => { setVista("listado"); setErrores({}); }} />
      <div style={{ padding: "0 2rem 2rem", maxWidth: "700px", margin: "0 auto" }}>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
          <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#7B5BF2", marginBottom: "4px" }}>Trazabilidad automática</h3>
          <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>Creado por: <strong style={{ color: texto }}>{usuario?.nombre}</strong> — {usuario?.rol}</p>
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#09184D" }}>Identificación</h3>
          <span style={lbl}>Proceso *</span>
          <select onChange={e => setForm(f => ({ ...f, proceso: e.target.value }))} style={{ ...inp, borderColor: errores.proceso ? "#e63946" : borde }} defaultValue="Impresión Offset">
            {procesosGraficos.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {maquinas.length > 0 && (
            <>
              <span style={lbl}>Vincular a máquina cargada (opcional — habilita comparar NPR entre equipos del mismo proceso)</span>
              <select onChange={e => {
                const id = e.target.value ? Number(e.target.value) : null;
                const m = maquinas.find(x => x.id === id);
                setForm(f => ({ ...f, maquinaId: id, maquinaNombre: m ? (m.nombre + (m.modelo ? ` — ${m.modelo}` : "")) : null }));
              }} style={inp}>
                <option value="">— Sin vincular —</option>
                {maquinas.map(m => <option key={m.id} value={m.id}>{m.nombre}{m.modelo ? ` — ${m.modelo}` : ""}</option>)}
              </select>
            </>
          )}
          {[{ key: "producto", label: "Producto *" }, { key: "operacion", label: "Operación / Etapa *" }].map(({ key, label }) => (
            <div key={key}>
              <span style={lbl}>{label}</span>
              <input onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, borderColor: errores[key] ? "#e63946" : borde }} />
              {errores[key] && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores[key]}</p>}
            </div>
          ))}
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#e63946" }}>Análisis de falla</h3>
          {[{ key: "modofalla", label: "Modo de falla *", placeholder: "Ej: Impresión fuera de registro" }, { key: "efecto", label: "Efecto de la falla *", placeholder: "Ej: Producto rechazado por cliente" }, { key: "causa", label: "Causa *", placeholder: "Ej: Desajuste de rodillos" }, { key: "controlesActuales", label: "Controles actuales existentes", placeholder: "Ej: Inspección visual cada 500 pliegos" }].map(({ key, label, placeholder }) => (
            <div key={key}>
              <span style={lbl}>{label}</span>
              <textarea rows={2} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, resize: "none", borderColor: errores[key] ? "#e63946" : borde }} placeholder={placeholder} />
              {errores[key] && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores[key]}</p>}
            </div>
          ))}
        </div>
        <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#3b82f6" }}>Puntuaciones (1 a 10) *</h3>
          <p style={{ fontSize: "12px", color: subtexto, marginBottom: "12px" }}>S = Severidad · O = Ocurrencia · D = Detección</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
            {[{ key: "severidad", label: "S — Severidad" }, { key: "ocurrencia", label: "O — Ocurrencia" }, { key: "deteccion", label: "D — Detección" }].map(({ key, label }) => (
              <div key={key}>
                <span style={{ ...lbl, marginTop: 0 }}>{label}</span>
                <input type="number" min="1" max="10" onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ ...inp, textAlign: "center", fontWeight: "700", fontSize: "16px", borderColor: errores[key] ? "#e63946" : borde }} placeholder="1-10" />
                {errores[key] && <p style={{ color: "#e63946", fontSize: "10px", margin: "2px 0 0" }}>{errores[key]}</p>}
              </div>
            ))}
          </div>
          {(form.severidad && form.ocurrencia && form.deteccion) && (
            <div style={{ marginTop: "16px", padding: "12px", borderRadius: "10px", background: `${nivelNPR(npr).color}22`, border: `2px solid ${nivelNPR(npr).color}`, textAlign: "center" }}>
              <p style={{ fontSize: "12px", color: subtexto, margin: "0 0 4px" }}>NPR calculado</p>
              <p style={{ fontSize: "28px", fontWeight: "700", color: nivelNPR(npr).color, margin: "0 0 4px" }}>{npr}</p>
              <p style={{ fontSize: "12px", fontWeight: "600", color: nivelNPR(npr).color, margin: 0 }}>{nivelNPR(npr).label}</p>
            </div>
          )}
        </div>
        <div style={card}>
          <h3 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#22c55e" }}>Acción y responsable</h3>
          <span style={lbl}>Acción recomendada {npr >= NPR_CRITICO ? "* (OBLIGATORIA por NPR crítico)" : ""}</span>
          <textarea rows={3} onChange={e => setForm(f => ({ ...f, accionRecomendada: e.target.value }))} style={{ ...inp, resize: "none" }} placeholder="Definí la acción para reducir el riesgo..." />
          <span style={lbl}>Responsable *</span>
          <input onChange={e => setForm(f => ({ ...f, responsable: e.target.value }))} style={{ ...inp, borderColor: errores.responsable ? "#e63946" : borde }} />
          {errores.responsable && <p style={{ color: "#e63946", fontSize: "11px", margin: "2px 0 0" }}>{errores.responsable}</p>}
          <span style={lbl}>Fecha compromiso</span>
          <input type="date" onChange={e => setForm(f => ({ ...f, fechaCompromiso: e.target.value }))} style={inp} />
        </div>
        <button onClick={guardar} style={{ width: "100%", padding: "14px", background: "#7B5BF2", color: "white", border: "none", borderRadius: "10px", fontSize: "15px", fontWeight: "600", cursor: "pointer", marginTop: "1rem" }}>
          Guardar FMEA
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="fmea" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="fmea" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>FMEA</span>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => exportarCSV(registrosFiltrados, "FMEA")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📊 Excel</button>
          <button onClick={() => exportarPDF(registrosFiltrados, "FMEA — Análisis de Riesgo")}
            style={{ padding: "6px 10px", background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", color: "white" }}>📄 PDF</button>
          {esAdmin && <button onClick={() => setVista("nueva")}
            style={{ padding: "6px 14px", background: "#e63946", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "600" }}>+ Nuevo FMEA</button>}
        </div>
      </div>
      <div style={{ padding: "1rem 1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Total", resumen.total, "#7B5BF2"], ["Críticos NPR≥100", resumen.criticos, "#e63946"], ["Abiertos", resumen.abiertos, "#f59e0b"], ["NPR Promedio", resumen.nprPromedio, "#3b82f6"]].map(([lab, val, col]) => (
            <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${lab === "Críticos NPR≥100" && resumen.criticos > 0 ? "#e63946" : borde}`, textAlign: "center" }}>
              <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "8px" }}>
          {[["todos", "Todos los tipos"], ["proceso", "🏭 Proceso"], ["maquina", "⚙️ Máquina"]].map(([val, lab]) => (
            <button key={val} onClick={() => setFiltroTipo(val)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroTipo === val ? "2px solid #3b82f6" : `1px solid ${borde}`, background: filtroTipo === val ? "rgba(59,130,246,.1)" : cardBg, color: filtroTipo === val ? "#3b82f6" : subtexto }}>{lab}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1rem" }}>
          {[["todos", "Todos"], ["criticos", "🔴 Críticos"], ["abierto", "Abiertos"], ["en_proceso", "En proceso"], ["cerrado", "Cerrados"]].map(([val, lab]) => (
            <button key={val} onClick={() => setFiltro(val)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtro === val ? "2px solid #7B5BF2" : `1px solid ${borde}`, background: filtro === val ? "rgba(123,91,242,.1)" : cardBg, color: filtro === val ? "#7B5BF2" : subtexto }}>{lab}</button>
          ))}
        </div>
        {registrosFiltrados.length === 0
          ? <div style={{ textAlign: "center", padding: "4rem" }}>
              <p style={{ fontSize: "40px" }}>🔍</p>
              <p style={{ fontSize: "15px", color: texto }}>No hay registros FMEA</p>
              <p style={{ fontSize: "13px", color: subtexto }}>Creá el primero para gestionar riesgos</p>
            </div>
          : registrosFiltrados.map(r => {
              const cantControles = obtenerControlesVinculados(r.idFMEA).length;
              return (
                <div key={r.id} onClick={() => { setDetalle(r); setVista("detalle"); }}
                  style={{ background: r.critico ? (oscuro ? "#1a0505" : "#fff5f5") : cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: `1px solid ${r.critico ? "#e63946" : borde}`, marginBottom: "10px", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(123,91,242,0.15)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <div>
                      <span style={{ fontWeight: "700", color: r.critico ? "#e63946" : "#7B5BF2", fontSize: "13px" }}>{r.idFMEA}</span>
                      <span style={{ marginLeft: "10px", fontSize: "13px", color: texto, fontWeight: "600" }}>{tipoDe(r) === "maquina" ? r.maquinaNombre : r.proceso}</span>
                      <span style={{ marginLeft: "8px", fontSize: "10px", fontWeight: "700", color: tiposFMEA[tipoDe(r)].color }}>{tiposFMEA[tipoDe(r)].label}</span>
                      {r.maquinaNombre && tipoDe(r) !== "maquina" && <span style={{ marginLeft: "8px", fontSize: "10px", color: subtexto }}>⚙️ {r.maquinaNombre}</span>}
                      <p style={{ fontSize: "12px", color: subtexto, marginTop: "4px" }}>{r.modofalla}</p>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
                      <span style={{ fontSize: "13px", fontWeight: "700", color: nivelNPR(r.npr).color, background: `${nivelNPR(r.npr).color}22`, padding: "2px 8px", borderRadius: "8px" }}>NPR {r.npr ?? "—"}</span>
                      <span style={{ fontSize: "11px", fontWeight: "600", color: estadosFMEA[r.estado]?.color }}>{estadosFMEA[r.estado]?.label}</span>
                      {cantControles > 0 && <span style={{ fontSize: "10px", color: "#3b82f6", fontWeight: "600" }}>🔗 {cantControles} control{cantControles > 1 ? "es" : ""}</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: "6px", fontSize: "11px", color: oscuro ? "#7B5BF2" : "#9b8cc4" }}>
                    Responsable: {r.responsable} · {r.fechaCreacion?.slice(0, 10)} · {r.idRiesgo || ""}
                  </div>
                </div>
              );
            })
        }
      </div>
    </div>
  );
}